import React, { useEffect, useMemo, useState } from "react";
import {
    Clock,
    Loader2,
    Mail,
    MailCheck,
    MailPlus,
    Pencil,
    Play,
    Plus,
    RefreshCw,
    Trash2,
    Search,
    CheckCircle2,
    AlertCircle,
    FileSpreadsheet,
    FileText,
    Users,
    Calendar,
    Building2,
    UserCheck,
    X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { apiClient } from "../../utils/apiClient";
import { mailApi } from "../../services/mailApi";
import { safeJsonParseArray } from "./mailUtils";
import MailSubscriptionModal from "./MailSubscriptionModal";

export const REPORT_LABELS = {
    raw_transitions: "Ham Geçişler Raporu",
    entry_exit_summary: "Giriş-Çıkış Özet Raporu",
    puantaj_raporu: "Genel Puantaj ve Mesai Raporu",
    daily_attendance: "Günlük Yoklama ve Mesai Detay Raporu",
    latecomers_report: "Geç Gelenler Raporu",
    early_leavers_report: "Erken Çıkanlar Raporu",
    absenteeism_report: "Devamsızlık (Gelmeyenler) Raporu",
    overtime_report: "Fazla Mesai Raporu",
    failed_pass_attempts: "Yetkisiz / Başarısız ve Konum Dışı Geçişler Raporu",
    leaves_report: "İzin Kullanım Hareketleri Raporu",
    leave_balance_report: "Yıllık İzin Bakiye Raporu",
    terminated_employees: "İşten Ayrılan Personeller Raporu",
    personnel_list: "Sicil Listesi Raporu",
    personal_info: "Özlük Bilgileri Raporu",
    legal_consent_report: "KVKK / Aydınlatma Onay Raporu",
};

const DYNAMIC_REPORT_LABELS = {};

export function reportLabel(t) {
    const key = String(t || "").trim();
    return DYNAMIC_REPORT_LABELS[key] || REPORT_LABELS[key] || key || "-";
}

const PERIOD_LABELS = {
    previous_day: "Dünün Verisi",
    today: "Bugünün Verisi",
    previous_week: "Geçen Hafta",
    this_week: "Bu Hafta",
    previous_month: "Geçen Ay",
    this_month: "Bu Ay",
    previous_period: "Önceki Dönem",
    current_period: "Bu Dönem",
};

const DOW_LABELS = {
    0: "Pazar",
    1: "Pazartesi",
    2: "Salı",
    3: "Çarşamba",
    4: "Perşembe",
    5: "Cuma",
    6: "Cumartesi",
};

function reportFormatInfo(v) {
    if (v === null || v === undefined) return { label: "-", badgeCls: "bg-gray-100 text-gray-700" };
    const s = String(v).trim().toLowerCase();

    if (s === "1" || s === "pdf") {
        return {
            label: "PDF",
            badgeCls: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
        };
    }
    if (s === "2" || s === "excel" || s === "xlsx") {
        return {
            label: "Excel",
            badgeCls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
        };
    }
    if (s === "3" || s === "pdfandexcel" || s === "both" || s === "pdf+excel" || s === "pdfexcel") {
        return {
            label: "PDF + Excel",
            badgeCls: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800",
        };
    }
    return { label: String(v), badgeCls: "bg-gray-100 text-gray-700 border-gray-200" };
}

function scheduleTypeLabel(v) {
    if (v === null || v === undefined) return "-";
    const s = String(v).trim().toLowerCase();
    if (s === "1" || s === "daily" || s === "gunluk" || s === "günlük") return "Günlük";
    if (s === "2" || s === "weekly" || s === "haftalik" || s === "haftalık") return "Haftalık";
    if (s === "3" || s === "monthly" || s === "aylik" || s === "aylık") return "Aylık";
    if (s === "4" || s === "cron") return "Cron";
    return String(v) || "-";
}

function parseParamsObject(raw) {
    if (!raw) return {};
    try {
        const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
        return obj && typeof obj === "object" && !Array.isArray(obj) ? obj : {};
    } catch {
        return {};
    }
}

function formatDetailedSchedule(item) {
    const rawType = item.scheduleType ?? item.ScheduleType;
    const typeLabel = scheduleTypeLabel(rawType);
    const params = parseParamsObject(item.parametersJson ?? item.ParametersJson);
    const cron = item.cron ?? item.Cron;

    const sh = params.sendHour ?? item.sendHour ?? item.SendHour ?? 9;
    const sm = params.sendMinute ?? item.sendMinute ?? item.SendMinute ?? 0;
    const hhmm = `${String(sh).padStart(2, "0")}:${String(sm).padStart(2, "0")}`;

    if (typeLabel === "Cron") {
        return { main: "Özel Zamanlama", sub: cron || "-" };
    }
    if (typeLabel === "Haftalık") {
        const dow = params.weeklyDow ?? item.weeklyDow ?? 1;
        return { main: `Haftalık • ${hhmm}`, sub: `Her ${DOW_LABELS[dow] || "Pazartesi"}` };
    }
    if (typeLabel === "Aylık") {
        const dom = params.monthlyDom ?? item.monthlyDom ?? 1;
        return { main: `Aylık • ${hhmm}`, sub: `Her ayın ${dom}. günü` };
    }

    const weekdays = Array.isArray(params.allowedWeekdays) ? params.allowedWeekdays : [];
    let daysSub = "Her gün";
    if (weekdays.length === 5 && [1, 2, 3, 4, 5].every((d) => weekdays.includes(d))) {
        daysSub = "Hafta içi (Pzt-Cum)";
    } else if (weekdays.length === 6 && [1, 2, 3, 4, 5, 6].every((d) => weekdays.includes(d))) {
        daysSub = "Pzt - Cmt (Pazar hariç)";
    } else if (weekdays.length > 0 && weekdays.length < 7) {
        daysSub = `${weekdays.length} gün aktif`;
    }

    return { main: `Günlük • ${hhmm}`, sub: daysSub };
}

function extractRecipients(item) {
    const to = safeJsonParseArray(item.recipientsToJson ?? item.RecipientsToJson)
        .concat(Array.isArray(item.recipientsTo) ? item.recipientsTo : [])
        .concat(Array.isArray(item.RecipientsTo) ? item.RecipientsTo : [])
        .map((x) => String(x || "").trim())
        .filter(Boolean);

    const cc = safeJsonParseArray(item.recipientsCcJson ?? item.RecipientsCcJson)
        .concat(Array.isArray(item.recipientsCc) ? item.recipientsCc : [])
        .concat(Array.isArray(item.RecipientsCc) ? item.RecipientsCc : [])
        .map((x) => String(x || "").trim())
        .filter(Boolean);

    return {
        to: Array.from(new Set(to)),
        cc: Array.from(new Set(cc)),
    };
}

function useMailPerms() {
    const { permissions = [] } = useAuth() || {};
    const has = (k) => permissions?.includes(k);
    return {
        canView: has("mail_subscriptions:view") || has("mail_subscriptions:manage"),
        canManage: has("mail_subscriptions:manage"),
        canRun: has("mail_subscriptions:run") || has("mail_subscriptions:manage"),
    };
}

function fmtDate(v) {
    if (!v) return "-";
    try {
        return new Date(v).toLocaleString("tr-TR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return String(v);
    }
}

function normalizeBool(v) {
    return !!(v === true || v === 1 || v === "1" || v === "true");
}

export default function MailSubscriptionsTab() {
    const perms = useMailPerms();

    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [systemStatus, setSystemStatus] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState(null); // { type: 'success'|'error', text: string }
    const [saving, setSaving] = useState(false);
    const [runningId, setRunningId] = useState(null);

    const [openModal, setOpenModal] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [editItem, setEditItem] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // all|active|passive

    const canCreate = perms.canManage;
    const canEdit = perms.canManage;
    const canDelete = perms.canManage;
    const canRun = perms.canRun;

    function showToast(text, type = "success") {
        setNotice({ text, type });
        setTimeout(() => {
            setNotice((prev) => (prev?.text === text ? null : prev));
        }, 4500);
    }

    async function load() {
        if (!perms.canView) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError("");
        try {
            const [data, sys, catalog] = await Promise.all([
                mailApi.listSubscriptions(),
                mailApi.getSystemStatus().catch(() => null),
                apiClient("Report/catalog?includeCustom=true").catch(() => []),
            ]);
            if (Array.isArray(catalog)) {
                catalog.forEach((m) => {
                    if (m?.reportType && m?.title) {
                        DYNAMIC_REPORT_LABELS[m.reportType] = m.title;
                    }
                });
            }
            const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
            setItems(list);
            if (sys) setSystemStatus(sys);
        } catch (e) {
            setError(e?.message || "Abonelikler yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, [perms.canView]);

    const stats = useMemo(() => {
        const total = items.length;
        const active = items.filter((x) => normalizeBool(x.isEnabled ?? x.IsEnabled)).length;
        const passive = total - active;
        return { total, active, passive };
    }, [items]);

    const sorted = useMemo(() => {
        const arr = [...(items || [])];
        arr.sort((a, b) => {
            const aa = normalizeBool(a.isEnabled ?? a.IsEnabled);
            const bb = normalizeBool(b.isEnabled ?? b.IsEnabled);
            if (aa !== bb) return aa ? -1 : 1;
            const an = (a.name ?? a.Name ?? "").localeCompare(b.name ?? b.Name ?? "", "tr");
            return an;
        });
        return arr;
    }, [items]);

    const filtered = useMemo(() => {
        const q = String(query || "").trim().toLowerCase();
        return (sorted || []).filter((x) => {
            const isEnabled = normalizeBool(x.isEnabled ?? x.IsEnabled);
            if (statusFilter === "active" && !isEnabled) return false;
            if (statusFilter === "passive" && isEnabled) return false;

            if (!q) return true;
            const name = String(x.name ?? x.Name ?? "").toLowerCase();
            const reportType = reportLabel(x.reportType ?? x.ReportType ?? "").toLowerCase();
            const reportFormat = reportFormatInfo(x.reportFormat ?? x.ReportFormat).label.toLowerCase();
            const scheduleType = scheduleTypeLabel(x.scheduleType ?? x.ScheduleType ?? "").toLowerCase();
            const { to, cc } = extractRecipients(x);
            const recipientsText = [...to, ...cc].join(" ").toLowerCase();

            return (
                name.includes(q) ||
                reportType.includes(q) ||
                reportFormat.includes(q) ||
                scheduleType.includes(q) ||
                recipientsText.includes(q)
            );
        });
    }, [sorted, query, statusFilter]);

    function openCreate() {
        setModalMode("create");
        setEditItem(null);
        setOpenModal(true);
    }

    function openEdit(item) {
        setModalMode("edit");
        setEditItem(item);
        setOpenModal(true);
    }

    async function handleSave(payload) {
        if (!perms.canManage) return;
        setSaving(true);
        setError("");

        try {
            if (modalMode === "create" && payload?.__bulkCreate) {
                const baseName = payload?.name ?? payload?.Name ?? "Abonelik";
                const reports =
                    Array.isArray(payload.__bulkReports) && payload.__bulkReports.length
                        ? payload.__bulkReports
                        : [payload.reportType ?? payload.ReportType];

                const base = { ...payload };
                delete base.__bulkCreate;
                delete base.__bulkReports;

                const rawFormat = payload.reportFormat ?? payload.ReportFormat ?? 1;
                const finalFormatInt = (() => {
                    const s = String(rawFormat ?? "").trim().toLowerCase();
                    if (
                        rawFormat === 3 ||
                        s === "3" ||
                        s === "both" ||
                        s === "pdf+excel" ||
                        s === "pdfexcel" ||
                        s === "pdfandexcel"
                    )
                        return 3;
                    if (rawFormat === 2 || s === "2" || s === "excel" || s === "xlsx") return 2;
                    return 1;
                })();

                for (const rt of reports) {
                    const nameSuffix = `${reportLabel(rt)}${finalFormatInt === 3 ? " (PDF+Excel)" : ""}`;
                    const finalName =
                        reports.length > 1 || finalFormatInt === 3 ? `${baseName} — ${nameSuffix}` : baseName;

                    const p = {
                        ...base,
                        name: finalName,
                        Name: finalName,
                        reportType: rt,
                        ReportType: rt,
                        reportFormat: finalFormatInt,
                        ReportFormat: finalFormatInt,
                    };

                    await mailApi.createSubscription(p);
                }

                setOpenModal(false);
                showToast(`${reports.length} adet mail aboneliği başarıyla oluşturuldu.`);
                await load();
                return;
            }

            if (modalMode === "edit") {
                const id = payload?.id || payload?.Id || editItem?.id || editItem?.Id;
                await mailApi.updateSubscription(id, payload);
                showToast("Mail aboneliği başarıyla güncellendi.");
            } else {
                await mailApi.createSubscription(payload);
                showToast("Yeni mail aboneliği başarıyla oluşturuldu.");
            }
            setOpenModal(false);
            await load();
        } catch (e) {
            throw e;
        } finally {
            setSaving(false);
        }
    }

    async function confirmDelete() {
        if (!canDelete || !deleteTarget) return;
        const id = deleteTarget?.id ?? deleteTarget?.Id;
        if (!id) return;

        setDeleting(true);
        try {
            await mailApi.deleteSubscription(id);
            setDeleteTarget(null);
            showToast("Mail aboneliği silindi.");
            await load();
        } catch (e) {
            setError(e?.message || "Silme sırasında hata oluştu.");
        } finally {
            setDeleting(false);
        }
    }

    async function runNow(item) {
        if (!canRun) return;
        const id = item?.id ?? item?.Id;
        if (!id) return;
        setRunningId(id);
        setError("");
        try {
            await mailApi.runNow(id);
            showToast(`"${item.name ?? item.Name}" aboneliği gönderim kuyruğuna alındı.`);
            await load();
        } catch (e) {
            setError(e?.message || "Çalıştırma sırasında hata oluştu.");
        } finally {
            setRunningId(null);
        }
    }

    if (!perms.canView) {
        return (
            <div className="p-6 rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-amber-800 dark:text-amber-200 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 flex-none" />
                <span>Mail aboneliklerini görüntüleme yetkiniz bulunmamaktadır.</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Üst Başlık ve Aksiyon Barı */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3.5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-blue-500/20 flex-none">
                        <MailPlus className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Otomatik Rapor & Mail Abonelikleri</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Belirlediğiniz periyotlarda puantaj ve devam raporlarının ilgili kişilere otomatik e-posta olarak gönderilmesini yönetin.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2.5 self-end md:self-center">
                    <button
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-all"
                        title="Listeyi Yenile"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        Yenile
                    </button>

                    {canCreate && (
                        <button
                            onClick={openCreate}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-semibold shadow-md shadow-blue-500/20 transition-all"
                        >
                            <Plus className="w-4 h-4" />
                            Yeni Abonelik Oluştur
                        </button>
                    )}
                </div>
            </div>

            {/* KPI Özet Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                        <Mail className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Toplam Abonelik</div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                        <MailCheck className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Aktif Abonelik</div>
                        <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.active}</div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                        <Clock className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Pasif / Duraklatılan</div>
                        <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.passive}</div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 flex items-center gap-4">
                    <div
                        className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                            systemStatus?.isEnabled ?? systemStatus?.IsEnabled
                                ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                        }`}
                    >
                        <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Firma Mail Servisi</div>
                        <div className="text-sm font-bold text-gray-900 dark:text-white mt-0.5">
                            {systemStatus === null ? (
                                <span className="text-gray-400">Kontrol ediliyor...</span>
                            ) : systemStatus?.isEnabled ?? systemStatus?.IsEnabled ? (
                                <span className="text-emerald-600 dark:text-emerald-400">Otomatik Gönderim Açık</span>
                            ) : (
                                <span className="text-amber-600 dark:text-amber-400">Servis Kapalı</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bildirim / Hata Bannerları */}
            {notice && (
                <div
                    className={`p-4 rounded-2xl border flex items-center justify-between gap-3 text-sm font-medium transition-all ${
                        notice.type === "error"
                            ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800"
                            : "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800"
                    }`}
                >
                    <div className="flex items-center gap-2.5">
                        <CheckCircle2 className="w-5 h-5 flex-none" />
                        <span>{notice.text}</span>
                    </div>
                    <button onClick={() => setNotice(null)} className="p-1 rounded-lg hover:bg-black/5">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {error && (
                <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 flex items-center justify-between gap-3 text-sm">
                    <div className="flex items-center gap-2.5">
                        <AlertCircle className="w-5 h-5 flex-none" />
                        <span>{error}</span>
                    </div>
                    <button onClick={() => setError("")} className="p-1 rounded-lg hover:bg-black/5">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Arama ve Durum Filtre Çubuğu */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="relative flex-1 max-w-lg">
                    <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder="Abonelik adı, alıcı e-posta, rapor türü veya zamanlama ara..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {query && (
                        <button
                            onClick={() => setQuery("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <div className="inline-flex rounded-xl bg-gray-100 dark:bg-gray-900 p-1 border border-gray-200 dark:border-gray-700">
                        {[
                            { key: "all", label: `Tümü (${stats.total})` },
                            { key: "active", label: `Aktif (${stats.active})` },
                            { key: "passive", label: `Pasif (${stats.passive})` },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setStatusFilter(tab.key)}
                                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                    statusFilter === tab.key
                                        ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm"
                                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Abonelikler Tablosu */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50/80 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-700 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            <tr>
                                <th className="py-3.5 px-4">Abonelik & Kapsam</th>
                                <th className="py-3.5 px-4">Rapor & Format</th>
                                <th className="py-3.5 px-4">Alıcılar (To / Cc)</th>
                                <th className="py-3.5 px-4">Zamanlama</th>
                                <th className="py-3.5 px-4">Son / Sonraki Gönderim</th>
                                <th className="py-3.5 px-4">Durum</th>
                                <th className="py-3.5 px-4 text-right">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {loading ? (
                                Array.from({ length: 4 }).map((_, idx) => (
                                    <tr key={idx} className="animate-pulse">
                                        <td colSpan={7} className="p-4">
                                            <div className="h-5 bg-gray-100 dark:bg-gray-700 rounded-lg w-full" />
                                        </td>
                                    </tr>
                                ))
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="py-14 px-4 text-center">
                                        <div className="max-w-sm mx-auto space-y-2">
                                            <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-700 text-gray-400 flex items-center justify-center mx-auto">
                                                <MailPlus className="w-6 h-6" />
                                            </div>
                                            <div className="text-base font-semibold text-gray-800 dark:text-gray-200">
                                                Abonelik Kaydı Bulunamadı
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                Arama kriterlerinize uygun bir abonelik yok veya henüz hiç otomatik rapor aboneliği tanımlanmamış.
                                            </p>
                                            {canCreate && (
                                                <button
                                                    onClick={openCreate}
                                                    className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-all"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                    İlk Aboneliği Oluştur
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((x) => {
                                    const id = x.id ?? x.Id;
                                    const isEnabled = normalizeBool(x.isEnabled ?? x.IsEnabled);
                                    const reportType = x.reportType ?? x.ReportType;
                                    const fmt = reportFormatInfo(x.reportFormat ?? x.ReportFormat);
                                    const sched = formatDetailedSchedule(x);
                                    const { to, cc } = extractRecipients(x);
                                    const params = parseParamsObject(x.parametersJson ?? x.ParametersJson);
                                    const scope = params.targetScope || "all";
                                    const deptCount = Array.isArray(params.departmentIds) ? params.departmentIds.length : 0;
                                    const periodLabel = PERIOD_LABELS[params.periodPreset] || null;
                                    const lastRun = x.lastRunAt ?? x.LastRunAt;
                                    const nextRun = x.nextRunAt ?? x.NextRunAt;

                                    return (
                                        <tr
                                            key={id}
                                            className="hover:bg-blue-50/30 dark:hover:bg-gray-700/40 transition-colors"
                                        >
                                            {/* Abonelik Adı ve Kapsam */}
                                            <td className="py-3.5 px-4">
                                                <div className="font-semibold text-gray-900 dark:text-white">
                                                    {x.name ?? x.Name ?? `#${id}`}
                                                </div>
                                                <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                    {scope === "recipient_departments" ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 font-medium">
                                                            <Building2 className="w-3 h-3" />
                                                            Alıcının Kendi Bölümleri (Yetki Bazlı)
                                                        </span>
                                                    ) : scope === "departments" ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 font-medium">
                                                            <Building2 className="w-3 h-3" />
                                                            Seçili Bölümler ({deptCount} Bölüm)
                                                        </span>
                                                    ) : scope === "own" ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
                                                            <UserCheck className="w-3 h-3" />
                                                            Sadece Kendi Bilgilerim
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300 font-medium">
                                                            <Users className="w-3 h-3" />
                                                            Tüm Personeller
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Rapor ve Format */}
                                            <td className="py-3.5 px-4">
                                                <div className="font-medium text-gray-800 dark:text-gray-200">
                                                    {reportLabel(reportType)}
                                                </div>
                                                <div className="flex items-center gap-1.5 mt-1">
                                                    <span
                                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${fmt.badgeCls}`}
                                                    >
                                                        {fmt.label.includes("Excel") ? (
                                                            <FileSpreadsheet className="w-3 h-3" />
                                                        ) : (
                                                            <FileText className="w-3 h-3" />
                                                        )}
                                                        {fmt.label}
                                                    </span>
                                                    {periodLabel && (
                                                        <span className="text-[11px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-md">
                                                            {periodLabel}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Alıcılar (To / Cc) */}
                                            <td className="py-3.5 px-4">
                                                <div
                                                    className="flex flex-wrap items-center gap-1 max-w-xs"
                                                    title={`Alıcılar (To): ${to.join(", ")}${
                                                        cc.length ? `\nBilgi (Cc): ${cc.join(", ")}` : ""
                                                    }`}
                                                >
                                                    {to.length === 0 ? (
                                                        <span className="text-xs text-red-500">Alıcı yok</span>
                                                    ) : (
                                                        <>
                                                            {to.slice(0, 2).map((email) => (
                                                                <span
                                                                    key={email}
                                                                    className="px-2 py-0.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200/70 dark:border-blue-800 text-xs font-mono truncate max-w-[175px]"
                                                                >
                                                                    {email}
                                                                </span>
                                                            ))}
                                                            {to.length > 2 && (
                                                                <span className="px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-semibold">
                                                                    +{to.length - 2} kişi
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                    {cc.length > 0 && (
                                                        <span className="px-2 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[11px] font-medium">
                                                            +{cc.length} CC
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Zamanlama */}
                                            <td className="py-3.5 px-4 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5 font-medium text-gray-800 dark:text-gray-200">
                                                    <Clock className="w-4 h-4 text-blue-500 flex-none" />
                                                    <span>{sched.main}</span>
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 pl-5">
                                                    {sched.sub}
                                                </div>
                                            </td>

                                            {/* Son / Sonraki Gönderim */}
                                            <td className="py-3.5 px-4 whitespace-nowrap text-xs">
                                                <div className="text-gray-700 dark:text-gray-300">
                                                    <span className="text-gray-400 mr-1">Sonraki:</span>
                                                    <span className="font-semibold">{fmtDate(nextRun)}</span>
                                                </div>
                                                <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                    <span className="text-gray-400 mr-1">Son:</span>
                                                    <span>{fmtDate(lastRun)}</span>
                                                </div>
                                            </td>

                                            {/* Durum */}
                                            <td className="py-3.5 px-4 whitespace-nowrap">
                                                {isEnabled ? (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                        Aktif
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                                        Pasif
                                                    </span>
                                                )}
                                            </td>

                                            {/* İşlemler */}
                                            <td className="py-3.5 px-4 text-right whitespace-nowrap">
                                                <div className="inline-flex items-center justify-end gap-1.5">
                                                    {canRun && (
                                                        <button
                                                            type="button"
                                                            disabled={runningId === id}
                                                            onClick={() => runNow(x)}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 dark:text-emerald-300 text-xs font-semibold transition-all disabled:opacity-50"
                                                            title="Şimdi Çalıştır (Kuyruğa Al)"
                                                        >
                                                            {runningId === id ? (
                                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                            ) : (
                                                                <Play className="w-3.5 h-3.5" />
                                                            )}
                                                            Gönder
                                                        </button>
                                                    )}
                                                    {canEdit && (
                                                        <button
                                                            type="button"
                                                            onClick={() => openEdit(x)}
                                                            className="p-2 rounded-xl text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-all"
                                                            title="Aboneliği Düzenle"
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setDeleteTarget(x)}
                                                            className="p-2 rounded-xl text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/30 transition-all"
                                                            title="Aboneliği Sil"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Oluşturma / Düzenleme Modalı */}
            <MailSubscriptionModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSave={handleSave}
                saving={saving}
                mode={modalMode}
                initial={editItem}
            />

            {/* Modern Silme Onay Modalı */}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-xs"
                        onClick={() => (!deleting ? setDeleteTarget(null) : null)}
                    />
                    <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-6 space-y-4">
                        <div className="flex items-start gap-3.5">
                            <div className="w-11 h-11 rounded-2xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center flex-none">
                                <Trash2 className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                    Aboneliği Silmek İstiyor Musunuz?
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                                        "{deleteTarget.name ?? deleteTarget.Name}"
                                    </span>{" "}
                                    adlı mail aboneliği pasife alınarak kaldırılacak ve bu işlem güvenlik denetim loglarına kaydedilecektir.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2.5 pt-2">
                            <button
                                type="button"
                                disabled={deleting}
                                onClick={() => setDeleteTarget(null)}
                                className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Vazgeç
                            </button>
                            <button
                                type="button"
                                disabled={deleting}
                                onClick={confirmDelete}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-md shadow-red-500/20 disabled:opacity-50"
                            >
                                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                                Evet, Aboneliği Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
